// The CSRF cookie is HttpOnly, so the token cannot be read from JavaScript.
// It is handed out by /panel/api/me instead and echoed back in a header —
// same double-submit check the server-rendered forms use.
let csrfToken = null;

export class ApiError extends Error {
  // The body travels with the error. A refusal that names *which* fields are
  // missing is useless if the caller only gets the word "incomplete", and
  // re-fetching to find out would be a second round trip for something the
  // first answer already carried.
  constructor(status, code, message, payload = null) {
    super(message ?? code ?? `HTTP ${status}`);
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

async function request(method, path, body) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (csrfToken && method !== 'GET') headers['X-CSRF-Token'] = csrfToken;

  const response = await fetch(`/panel/api${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    // Where the tool refuses a publisher, by code and never by field: the
    // message can carry a nickname or a title, and this is an analytics event.
    window.tips?.track('panel_error', { code: String(payload?.error ?? response.status) });
    throw new ApiError(response.status, payload?.error, payload?.message, payload);
  }
  return payload;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body ?? {}),
  patch: (path, body) => request('PATCH', path, body ?? {}),
  del: (path) => request('DELETE', path),
};

// For requests that cannot go through the JSON helpers — a raw file upload
// sends the image bytes as the body, so it needs the header on its own.
export function csrfHeader() {
  return csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
}

export async function loadSession() {
  const me = await api.get('/me');
  csrfToken = me.csrf;
  return me;
}
